import { z } from 'zod';
import { LOCALES } from './locale.js';

export const PASSWORD_MIN_LENGTH = 10;
/** Upper bound keeps Argon2 input reasonable and blocks hash-DoS payloads. */
export const PASSWORD_MAX_LENGTH = 256;

const email = z.string().trim().toLowerCase().max(254).pipe(z.email());
// Length rules are checked by the password policy so the API can return
// specific error codes instead of a generic validation failure.
const password = z.string().min(1).max(1024);
const name = z.string().trim().min(1).max(120);

export const locale = z.enum(LOCALES);
export const userType = z.enum(['developer', 'business']);
export const tenantRole = z.enum(['owner', 'admin', 'member']);
export const workspaceRole = z.enum(['owner', 'editor', 'viewer']);
export const workspaceKind = z.enum(['chat', 'code']);
export const uuid = z.uuid();

export const signupBody = z.object({
  email,
  password,
  displayName: name,
  locale: locale.optional(),
  acceptTerms: z.literal(true),
  /** Phase 16a (D-259): campaign e-mails, optional and recorded with its time */
  marketingConsent: z.boolean().default(false),
});
/** Phase 16a (D-258): the browser's key to its sign-up, and the six digits mailed to the address */
const registration = z.string().regex(/^[A-Za-z0-9_-]{32,128}$/);
export const signupVerifyBody = z.object({ registration, code: z.string().trim().regex(/^\d{6}$/) });
export const signupResendBody = z.object({ registration });

/** a break-glass account also gives its six-digit TOTP code (Phase 13d) */
export const loginBody = z.object({
  email,
  password,
  totp: z.string().trim().regex(/^\d{6}$/).optional(),
  /** Phase 16a (D-260): "30 gün hatırla"; without it the session ends with the browser (at most SESSION_SHORT_HOURS) */
  remember: z.boolean().default(true),
});
/** Phase 13d: the company account, as typed (an email address or a directory user name) */
export const directoryLoginBody = z.object({
  username: z.string().trim().min(1).max(254),
  password,
  remember: z.boolean().default(false),
});
/** Phase 15c (D-164): the app's sign-in; `device` is the phone's model name ("iPhone"), shown under Oturumlar */
export const directoryTokenBody = z.object({
  username: z.string().trim().min(1).max(254),
  password,
  device: z.string().trim().min(1).max(60).regex(/^[\p{L}\p{N} .,()-]+$/u).optional(),
  // "30 gün hatırla", ticked by default in the app; unticked: a short session the app keeps only in memory
  remember: z.boolean().default(true),
});
export const tokenBody = z.object({ token: z.string().min(16).max(256) });
export const passwordResetRequestBody = z.object({ email });
export const passwordResetConfirmBody = z.object({ token: z.string().min(16).max(256), password });
export const passwordChangeBody = z.object({ currentPassword: password, newPassword: password });

export const updateMeBody = z
  .object({
    displayName: name.optional(),
    locale: locale.optional(),
    userType: userType.optional(),
    onboarded: z.literal(true).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'empty_update' });

export const switchTenantBody = z.object({ tenantId: uuid });

/** An IANA time zone the runtime knows ("Europe/Istanbul"). */
export const timeZone = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .refine((value) => {
    try {
      new Intl.DateTimeFormat('en', { timeZone: value });
      return true;
    } catch {
      return false;
    }
  }, { message: 'unknown time zone' });
export const updateTenantBody = z
  .object({
    name: name.optional(),
    allowFullAccess: z.boolean().optional(),
    memoryAllowed: z.boolean().optional(),
    requireCodeApproval: z.boolean().optional(),
    trashRetentionDays: z.number().int().min(1).max(365).nullable().optional(),
    defaultTimeZone: timeZone.optional(),
    /** D-58: words + meaning, or words only (no document text leaves our servers) */
    documentSearch: z.enum(['hybrid', 'keyword']).optional(),
    /** MM-006: the chat models members may use; null: all */
    modelAllowlist: z.array(uuid).max(200).nullable().optional(),
    /** MM-022: members may add their own providers */
    allowPersonalProviders: z.boolean().optional(),
  })
  .refine((body) => Object.values(body).some((value) => value !== undefined), { message: 'nothing to update' });

// Phase 13: the admin panel -----------------------------------------------------------------------

export const createOrganizationBody = z.object({ name });
export const removeMemberQuery = z.object({ transferTo: uuid.optional() });
export const adminDaysQuery = z.object({ days: z.coerce.number().int().min(1).max(365).default(30) });
// adminQuotasBody: admin.ts (Phase 13d-2)
export const auditQuery = z.object({
  /** the last entry of the previous page (entries of one transaction share their time) */
  before: uuid.optional(),
  action: z.string().trim().max(64).regex(/^[a-z_.]*$/).optional(),
  actorUserId: uuid.optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});
export const auditExportQuery = z.object({ days: z.coerce.number().int().min(1).max(730).default(90) });
export const mcpOauthClientBody = z.object({
  clientId: z.string().trim().min(1).max(500),
  /** write-only; kept encrypted, never returned */
  clientSecret: z.string().min(1).max(2000).optional(),
});
export const platformInternalBody = z.object({ allowed: z.boolean() });
/** SA-069: a platform provider the operator adds in the UI (.env still seeds them at the first start) */
export const platformProviderBody = z.object({
  name: z.string().trim().min(1).max(80),
  kind: z.enum(['openai', 'openai_compatible', 'custom']),
  baseUrl: z.string().trim().min(8).max(500),
  /** write-only; shown once in the form, never returned */
  apiKey: z.string().min(1).max(500).optional(),
  /** the gateway manages its GPU slots from the provider's load (vLLM) */
  capacityManaged: z.boolean().optional(),
  /** ask the provider not to log content (LiteLLM, Langfuse) */
  logOptOut: z.boolean().optional(),
});
export const platformRouteBody = z.object({ modelRef: uuid.nullable() });
/** Phase 13g (D-136): "Bağlantıyı dene" before a platform provider (an operator's or the kurumsal installation's) is added */
export const platformProviderDraftBody = z.object({
  kind: z.enum(['openai', 'openai_compatible', 'custom']),
  baseUrl: z.string().trim().min(8).max(500),
  apiKey: z.string().min(1).max(500).optional(),
  modelId: z.string().trim().min(1).max(200).optional(),
});
/** Phase 13g (D-138): Kapasite's day */
export const capacityQuery = z.object({ day: z.enum(['today', 'yesterday']).default('today') });

export const createInviteBody = z.object({
  email,
  role: z.enum(['admin', 'member']).default('member'),
});
export const updateMemberBody = z.object({ role: tenantRole });
/** Phase 13d: an admin turns a member's access off at once, or on again */
export const memberAccessBody = z.object({ active: z.boolean() });
/** Phase 13d: the name a domain's shared workspace shows; null: the directory's name */
export const identityDomainBody = z.object({ displayName: z.string().trim().min(1).max(120).nullable() });
/** Phase 15a (D-150): a directory team opened before its first member signs in (domain_hr, "İnsan Kaynakları") */
export const identityDomainOpenBody = z.object({
  name: z.string().trim().min(1).max(120).regex(/^[A-Za-z0-9_.-]+$/),
  displayName: z.string().trim().min(1).max(120).optional(),
});

export const createWorkspaceBody = z.object({ name, kind: workspaceKind });
export const updateWorkspaceBody = z
  .object({ name: name.optional(), description: z.string().trim().max(300).nullable().optional() })
  .refine((body) => body.name !== undefined || body.description !== undefined, { message: 'give a name or a description' });
export const addWorkspaceMemberBody = z.object({ userId: uuid, role: z.enum(['editor', 'viewer']) });

export const createProjectBody = z.object({
  name,
  rootPath: z.string().trim().max(240).optional(),
  repoUrl: z.url({ protocol: /^https?$/ }).max(500).optional(),
});
export const updateProjectBody = z
  .object({ name: name.optional() })
  .refine((v) => Object.keys(v).length > 0, { message: 'empty_update' });

export const createConversationBody = z.object({
  title: z.string().trim().min(1).max(200).optional(),
  projectId: uuid.optional(),
  /** the home page's uploads made before the first message: the conversation takes them over (and their id) */
  draftId: uuid.optional(),
});
export const updateConversationBody = z
  .object({
    title: z.string().trim().min(1).max(200).optional(),
    pinned: z.boolean().optional(),
    archived: z.boolean().optional(),
    groupId: z.uuid().nullable().optional(),
    modelRef: z.uuid().nullable().optional(),
    /** QW-613: thinking on ("auto") or off for this conversation */
    thinking: z.enum(['auto', 'off']).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'empty_update' });

export const catalogueQuery = z.object({
  experience: z.enum(['chat', 'code']).optional(),
  workspaceId: z.uuid().optional(),
  view: z.enum(['active', 'archived', 'trash']).default('active'),
  groupId: z.uuid().optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(30),
});
export const searchQuery = z.object({
  q: z.string().trim().min(1).max(200),
  experience: z.enum(['chat', 'code']).optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export const branchBody = z.object({ atSeq: z.number().int().positive().optional() });

export const groupBody = z.object({
  name: z.string().trim().min(1).max(80),
  color: z.enum(['red', 'orange', 'yellow', 'green', 'blue', 'purple', 'gray']).default('blue'),
});
export const updateGroupBody = z
  .object({
    name: z.string().trim().min(1).max(80).optional(),
    color: z.enum(['red', 'orange', 'yellow', 'green', 'blue', 'purple', 'gray']).optional(),
    sort: z.number().int().min(0).max(10_000).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'empty_update' });

export const rewindBody = z.object({ toSeq: z.number().int().positive(), files: z.boolean().default(false) });
export const editMessageBody = z.object({ text: z.string().trim().min(1).max(20_000) });
export const regenerateBody = z.object({ modelRef: z.uuid().optional() });
export const sideQuestionBody = z.object({ text: z.string().trim().min(1).max(4000) });
export const forkBody = z.object({ directive: z.string().trim().min(1).max(4000) });
export const feedbackBody = z.object({
  messageId: z.string().min(1).max(64),
  rating: z.union([z.literal(1), z.literal(-1)]),
  note: z.string().trim().max(2000).optional(),
});
export const memoryBody = z.object({ content: z.string().trim().min(1).max(2000), workspaceId: z.uuid().nullable().optional() });
export const updateMemoryBody = z
  .object({ content: z.string().trim().min(1).max(2000).optional(), workspaceId: z.uuid().nullable().optional() })
  .refine((v) => Object.keys(v).length > 0, { message: 'empty_update' });
export const preferencesBody = z
  .object({
    memoryEnabled: z.boolean().optional(),
    responseLanguage: z.enum(['auto', 'tr', 'en']).optional(),
    customInstructions: z.string().trim().max(4000).nullable().optional(),
    /** the user's own choice; null follows the browser */
    timeZone: timeZone.nullable().optional(),
    /** the browser's, sent by the app when it differs */
    detectedTimeZone: timeZone.optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'empty_update' });
export const exportQuery = z.object({ format: z.enum(['md', 'json']).default('md'), locale: z.enum(['tr', 'en']).optional() });
/**
 * "Verilerimi sil": a local account confirms with its password; a company-directory account has no password
 * here and types its e-mail address instead (D-187).
 */
export const deleteMyDataBody = z
  .object({ password: z.string().min(1).max(200).optional(), email: z.string().trim().min(1).max(320).optional(), confirm: z.literal(true) })
  .refine((body) => body.password !== undefined || body.email !== undefined, { message: 'password or email is required', path: ['password'] });

/** D-187: the typed address matches the account's (case and surrounding spaces do not count) */
export const sameEmail = (typed: string, account: string) => typed.trim().toLocaleLowerCase('en') === account.trim().toLocaleLowerCase('en');

export const MESSAGE_MAX_LENGTH = 20_000;
export const postMessageBody = z.object({
  text: z.string().trim().min(1).max(MESSAGE_MAX_LENGTH),
  attachmentIds: z.array(z.uuid()).max(20).optional(),
});

export const listQuery = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export const eventsQuery = z.object({
  after: z.coerce.number().int().min(0).default(0),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});

export type SignupBody = z.infer<typeof signupBody>;
export type LoginBody = z.infer<typeof loginBody>;
export type DirectoryLoginBody = z.infer<typeof directoryLoginBody>;
export type UpdateMeBody = z.infer<typeof updateMeBody>;
export type CreateWorkspaceBody = z.infer<typeof createWorkspaceBody>;
export type CreateProjectBody = z.infer<typeof createProjectBody>;
export type CreateConversationBody = z.infer<typeof createConversationBody>;
export type TenantRole = z.infer<typeof tenantRole>;
export type WorkspaceRole = z.infer<typeof workspaceRole>;
export type WorkspaceKind = z.infer<typeof workspaceKind>;
export type UserType = z.infer<typeof userType>;

// Providers and models (Phase 7; UI in Phase 13) -------------------------------

export const providerKind = z.enum(['openai', 'openai_compatible', 'custom']);
export const providerScope = z.enum(['tenant', 'user']);
export const MODEL_CALL_TYPES = [
  'main',
  'code',
  'subagent',
  'helper',
  'compaction',
  'title',
  'summary',
  'explain',
  'doc_extract',
  'web_extract',
  'classifier',
  'memory',
  'suggestion',
] as const;
/** Document search's tasks (Phase 12), served by models of their own kind; the installation and the company choose them */
export const VECTOR_ROUTE_TYPES = ['embedding', 'rerank', 'ocr'] as const;
/** Every task a model is chosen for (MM-009); a person's own choice is never a vector task's */
export const ROUTE_TASK_TYPES = [...MODEL_CALL_TYPES, ...VECTOR_ROUTE_TYPES] as const;
export const routeCallType = z.enum(ROUTE_TASK_TYPES);
/** the kind of model a task needs */
export function taskModelKind(callType: string): 'chat' | 'embedding' | 'rerank' | 'ocr' {
  // a safety model ("guard", 16c.1) serves no task yet
  return (VECTOR_ROUTE_TYPES as readonly string[]).includes(callType) ? (callType as 'embedding' | 'rerank' | 'ocr') : 'chat';
}

const apiKey = z.string().trim().min(1).max(500);
const providerName = z.string().trim().min(1).max(80);
const baseUrl = z.string().trim().min(8).max(500);
const modelIdText = z.string().trim().min(1).max(200);

export const createProviderBody = z.object({
  scope: providerScope,
  name: providerName,
  kind: providerKind,
  baseUrl: baseUrl.optional(),
  apiKey: apiKey.optional(),
  /** custom providers: the model to register */
  modelId: modelIdText.optional(),
  contextWindow: z.number().int().min(1024).max(10_000_000).optional(),
});

export const updateProviderBody = z
  .object({
    name: providerName.optional(),
    baseUrl: baseUrl.optional(),
    apiKey: apiKey.optional(),
    clearApiKey: z.literal(true).optional(),
    enabled: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'empty_update' });

export const draftProviderTestBody = z.object({
  scope: providerScope,
  kind: providerKind,
  baseUrl: baseUrl.optional(),
  apiKey: apiKey.optional(),
  modelId: modelIdText.optional(),
});

export const providerTestBody = z.object({ modelId: modelIdText.optional() });

export const addModelBody = z.object({
  modelId: modelIdText,
  displayName: z.string().trim().min(1).max(120).optional(),
  contextWindowOverride: z.number().int().min(1024).max(10_000_000).optional(),
});

export const updateModelBody = z
  .object({
    displayName: z.string().trim().min(1).max(120).nullable().optional(),
    enabled: z.boolean().optional(),
    contextWindowOverride: z.number().int().min(1024).max(10_000_000).nullable().optional(),
    maxOutputTokens: z.number().int().min(1024).max(1_000_000).nullable().optional(),
    priceInputMicros: z.number().int().min(0).optional(),
    priceCachedInputMicros: z.number().int().min(0).optional(),
    priceOutputMicros: z.number().int().min(0).optional(),
    thinking: z.enum(['unknown', 'none', 'template_toggle', 'effort_toggle', 'always']).optional(),
    supportsTools: z.boolean().nullable().optional(),
    /** Phase 16c.1: the picker's line under the name ("dengeli, her iş için") */
    description: z.string().trim().min(1).max(120).nullable().optional(),
    /** Phase 16c.1: what the model is for; a chat model talks, the others embed, rerank, read pages or check safety */
    kind: z.enum(['chat', 'embedding', 'rerank', 'ocr', 'guard']).optional(),
    /** Phase 16c.1: "Sohbette", and in kurumsal "Kimler kullanabilir" (Herkes or the chosen teams) */
    chatOffered: z.boolean().optional(),
    chatAudience: z.enum(['everyone', 'teams']).optional(),
    teamIds: z.array(uuid).max(200).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'empty_update' })
  .refine((v) => v.chatAudience !== 'teams' || (v.teamIds !== undefined && v.teamIds.length > 0), { message: 'choose at least one team', path: ['teamIds'] });

export const setRouteBody = z.object({ scope: providerScope, modelRef: uuid });
export const routeScopeQuery = z.object({ scope: providerScope.default('user') });

export const usageQuery = z.object({
  days: z.coerce.number().int().min(1).max(366).default(30),
  groupBy: z.enum(['call_type', 'model_name', 'user_id', 'day']).default('call_type'),
  scope: z.enum(['me', 'tenant']).default('me'),
});

export const updateTenantProvidersBody = z.object({ allowPersonalProviders: z.boolean() });
